/**
 * V1.9.0 rounding policy for areas shown together (legend rows, A and B, Actual and Propuesta, totals, PNG).
 *
 * Rounding every row on its own can make a column add up to 0.01 more or less than its rounded total (two legends of
 * the same surface could then read 416.81 and 416.80 ha). Rows are therefore apportioned by LARGEST REMAINDER
 * (Hamilton): the shown total is the full-precision sum rounded once; every row starts from its value truncated to the
 * shown decimals, and the missing hundredths go to the rows with the largest discarded fraction (ties: larger value,
 * then earlier row). So the shown rows always add up to the shown total, and two columns of the same surface show the
 * same total. Only the presentation changes: geometries and full-precision areas are never modified.
 */
const EPSILON = 1e-9;

/**
 * `shownTotal` (optional): apportion to an already shown total instead — a group's rows to the group's shown subtotal,
 * so rows, subtotals and the grand total all reconcile.
 */
export function apportionRounded(values: readonly number[], decimals: number, shownTotal?: number): number[] {
  const scale = 10 ** Math.max(0, Math.min(6, Math.round(decimals)));
  const scaled = values.map((value) => Math.max(0, value) * scale);
  const floors = scaled.map((value) => Math.floor(value + EPSILON));
  const target = shownTotal === undefined ? Math.round(scaled.reduce((sum, value) => sum + value, 0) + EPSILON) : Math.round(shownTotal * scale);
  let missing = target - floors.reduce((sum, value) => sum + value, 0);
  const order = scaled.map((value, index) => ({ index, fraction: value - floors[index]!, value }))
    .sort((a, b) => b.fraction - a.fraction || b.value - a.value || a.index - b.index);
  for (const item of order) { if (missing <= 0) break; floors[item.index]! += 1; missing -= 1; }
  return floors.map((value) => value / scale);
}

/** Total shown for a column: the full-precision sum rounded once (equal to the sum of `apportionRounded`). */
export const roundedTotal = (values: readonly number[], decimals: number): number => {
  const scale = 10 ** Math.max(0, Math.min(6, Math.round(decimals)));
  return Math.round(values.reduce((sum, value) => sum + Math.max(0, value), 0) * scale + EPSILON) / scale;
};
